/**
 * Per-person notification preferences (KAN-82).
 *
 * Lives on the account page rather than under Administration, because these are
 * settings about *you* — unlike the alert thresholds, which are settings about
 * the firm and are Finance's. Somebody with no `alerts.manage` still needs this
 * screen, and putting the two together would have implied otherwise.
 *
 * ## What cannot be turned off
 *
 * There is no in-app toggle. The notification centre is the durable record of
 * every alert, and offering to disable it would be an offer the system has to
 * refuse — the retry design depends on the record existing whether or not a
 * message got out. The panel says so rather than leaving its absence to be
 * noticed.
 *
 * ## Channels that are not configured
 *
 * Their controls render disabled with an explanation rather than being hidden.
 * A hidden control reads as a missing feature; a disabled one that says "no
 * mail credentials on this deployment" is an answer.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DIGEST_FREQUENCIES,
  DIGEST_FREQUENCY_META,
  type AlertCode,
  type AlertSeverity,
  type DigestFrequency,
} from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { Alert as Banner } from '../ui/Alert'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'

interface Preferences {
  emailEnabled: boolean
  slackEnabled: boolean
  slackUserId: string | null
  digestFrequency: DigestFrequency
  quietHoursStartMinute: number | null
  quietHoursEndMinute: number | null
  mutedCodes: AlertCode[]
}

interface PreferencesResponse {
  preferences: Preferences
  channels: { email: { available: boolean }; slack: { available: boolean } }
  alertTypes: {
    code: AlertCode
    label: string
    description: string
    severity: AlertSeverity
  }[]
}

export function NotificationPreferences() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Preferences | null>(null)

  const query = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => apiFetch<PreferencesResponse>('/notifications/preferences'),
  })

  /**
   * The server's values seed the form once, and the form owns them afterwards.
   * Re-seeding on every refetch would discard whatever somebody was in the
   * middle of typing.
   */
  useEffect(() => {
    if (query.data && draft === null) setDraft(query.data.preferences)
  }, [query.data, draft])

  const save = useMutation({
    mutationFn: (next: Preferences) =>
      apiFetch('/notifications/preferences', {
        method: 'PATCH',
        body: JSON.stringify(next),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  if (!draft || !query.data) {
    return (
      /** The anchor the notification centre's "settings" link jumps to. */
      <div id="notifications">
        <Card>
          <CardHeader title="Notifications" />
          <CardBody>
            <p className="text-sm text-ink-600">Loading…</p>
          </CardBody>
        </Card>
      </div>
    )
  }

  const { channels, alertTypes } = query.data
  const quietOn = draft.quietHoursStartMinute !== null && draft.quietHoursEndMinute !== null

  const set = (patch: Partial<Preferences>) => setDraft({ ...draft, ...patch })

  return (
    <div id="notifications">
    <Card>
      <CardHeader
        title="Notifications"
        description="How you hear about exception alerts. The notification centre always has them; this decides what else reaches you."
        actions={
          <Button variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate(draft)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        }
      />
      <CardBody className="space-y-6">
        {save.isError && <Banner tone="error">{(save.error as Error).message}</Banner>}
        {save.isSuccess && <Banner tone="success">Saved.</Banner>}

        <div className="rounded-md border border-ink-200 bg-ink-50 px-4 py-3 text-sm text-ink-700">
          <strong className="text-brand-900">In-app is always on.</strong> Every alert you are
          eligible for lands in the notification centre whether or not an email or Slack message
          gets through — that is what makes a failed send a delivery problem rather than a lost
          alert.
        </div>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-ink-800">Channels</legend>

          <label className="flex items-start gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={draft.emailEnabled}
              disabled={!channels.email.available}
              onChange={(e) => set({ emailEnabled: e.target.checked })}
            />
            <span>
              Email
              {!channels.email.available && (
                <span className="block text-xs text-ink-500">
                  Unavailable — this deployment has no Google service account configured for
                  outbound mail.
                </span>
              )}
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={draft.slackEnabled}
              disabled={!channels.slack.available}
              onChange={(e) => set({ slackEnabled: e.target.checked })}
            />
            <span>
              Slack direct messages
              <span className="block text-xs text-ink-500">
                {channels.slack.available
                  ? 'Separate from the finance channel, which the firm configures per rule. This is Slack messaging you personally.'
                  : 'Unavailable — no Slack bot token is configured on this deployment.'}
              </span>
            </span>
          </label>

          {draft.slackEnabled && (
            <Field
              id="slack-user-id"
              label="Your Slack member ID"
              hint="Slack → your profile → More → Copy member ID. It is not your email: matching by address would send a client's figure to whoever holds that address in the workspace."
            >
              <TextInput
                id="slack-user-id"
                value={draft.slackUserId ?? ''}
                placeholder="U01ABC2DEF3"
                onChange={(e) => set({ slackUserId: e.target.value || null })}
              />
            </Field>
          )}
        </fieldset>

        <Field
          id="digest-frequency"
          label="Email frequency"
          hint={`${DIGEST_FREQUENCY_META[draft.digestFrequency].description} Critical alerts always arrive immediately, whatever this says.`}
        >
          <Select
            id="digest-frequency"
            value={draft.digestFrequency}
            onChange={(e) => set({ digestFrequency: e.target.value as DigestFrequency })}
          >
            {DIGEST_FREQUENCIES.map((value) => (
              <option key={value} value={value}>
                {DIGEST_FREQUENCY_META[value].label}
              </option>
            ))}
          </Select>
        </Field>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-ink-800">Quiet hours</legend>
          <p className="text-xs text-ink-600">
            Non-critical alerts raised in this window are held and delivered together when it ends,
            never discarded. Critical alerts — client money, and the firm running out of cash —
            come through regardless.
          </p>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={quietOn}
              onChange={(e) =>
                set(
                  e.target.checked
                    ? { quietHoursStartMinute: 1_320, quietHoursEndMinute: 420 }
                    : { quietHoursStartMinute: null, quietHoursEndMinute: null }
                )
              }
            />
            Hold non-critical alerts overnight
          </label>

          {quietOn && (
            <div className="grid max-w-md gap-4 sm:grid-cols-2">
              <Field id="quiet-start" label="From">
                <Select
                  id="quiet-start"
                  value={draft.quietHoursStartMinute ?? 1_320}
                  onChange={(e) => set({ quietHoursStartMinute: Number(e.target.value) })}
                >
                  {HALF_HOURS.map((minute) => (
                    <option key={minute} value={minute}>
                      {clockLabel(minute)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field id="quiet-end" label="Until" hint="Times are the firm's — America/New_York.">
                <Select
                  id="quiet-end"
                  value={draft.quietHoursEndMinute ?? 420}
                  onChange={(e) => set({ quietHoursEndMinute: Number(e.target.value) })}
                >
                  {HALF_HOURS.map((minute) => (
                    <option key={minute} value={minute}>
                      {clockLabel(minute)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-ink-800">Alert types</legend>
          <p className="text-xs text-ink-600">
            Muting stops the email and the Slack message. The alert still appears in your
            notification centre — this is where you would come to check what you muted.
          </p>

          {alertTypes.map((type) => {
            const muted = draft.mutedCodes.includes(type.code)
            return (
              <label
                key={type.code}
                className="flex items-start gap-2 border-t border-ink-100 pt-2 text-sm text-ink-700 first:border-t-0"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!muted}
                  onChange={(e) =>
                    set({
                      mutedCodes: e.target.checked
                        ? draft.mutedCodes.filter((code) => code !== type.code)
                        : [...draft.mutedCodes, type.code],
                    })
                  }
                />
                <span>
                  <span className="flex items-center gap-2">
                    {type.label}
                    {type.severity === 'critical' && <Badge tone="critical">Critical</Badge>}
                  </span>
                  <span className="block text-xs text-ink-500">{type.description}</span>
                </span>
              </label>
            )
          })}

          {alertTypes.length === 0 && (
            <p className="text-sm text-ink-600">
              Your role is not set to receive any alert type. Nothing here would reach you.
            </p>
          )}
        </fieldset>
      </CardBody>
    </Card>
    </div>
  )
}

/**
 * Half-hour steps, which is as fine as anybody sets a quiet window.
 *
 * A free time input would let somebody enter 22:07 and would need parsing,
 * validation and a timezone conversation. Forty-eight options in a select is
 * the whole feature.
 */
const HALF_HOURS = Array.from({ length: 48 }, (_, index) => index * 30)

function clockLabel(minute: number): string {
  const hour = Math.floor(minute / 60)
  const minutes = String(minute % 60).padStart(2, '0')
  return `${String(hour).padStart(2, '0')}:${minutes}`
}
